import { useState, useEffect } from "react";
import { useTemplatesStore } from "@/store/templatesStore";
import { useEmailBuilderStore } from "@/store/emailBuilderStore";
import { useNavigate } from "react-router-dom";
import type { EmailTemplate } from "@/types/email";

interface TemplatePickerProps {
  onClose: () => void;
}

export function TemplatePicker({ onClose }: TemplatePickerProps) {
  const navigate = useNavigate();
  const { templates, isLoading: templatesLoading, fetchTemplates } = useTemplatesStore();
  const { createDocument, createFromTemplate } = useEmailBuilderStore();
  
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isCreating) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose, isCreating]);

  const handleCreateBlank = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const doc = await createDocument("Untitled Email");
      
      // Don't await navigation - let it happen asynchronously
      // Component will unmount from navigation anyway
      navigate(`/campaigns/email/${doc.id}`);
      
      // Close modal immediately
      onClose();
    } catch (error) {
      console.error("Failed to create blank email:", error);
      const errorMsg = error instanceof Error ? error.message : "Failed to create blank email";
      setError(errorMsg);
      setIsCreating(false);
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;

    setIsCreating(true);
    setError(null);
    try {
      const template = templates.find((t) => t.id === selectedTemplate);
      const documentName = template ? `${template.name} Copy` : "Untitled Email";
      const doc = await createFromTemplate(selectedTemplate, documentName);
      
      // Don't await navigation - let it happen asynchronously
      // Component will unmount from navigation anyway
      navigate(`/campaigns/email/${doc.id}`);
      
      // Close modal immediately
      onClose();
    } catch (error) {
      console.error("Failed to create email from template:", error);
      const errorMsg = error instanceof Error ? error.message : "Failed to create email from template";
      setError(errorMsg);
      setIsCreating(false);
    }
  };

  // Get unique categories from templates
  const categories = Array.from(
    new Set(templates.map((t) => t.category))
  ).filter(Boolean);

  // Filter templates
  const filteredTemplates = templates.filter((template) => {
    const matchesCategory = categoryFilter === "all" || template.category === categoryFilter;
    const matchesSearch = searchQuery === "" || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div 
      className="lw-template-picker-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isCreating) {
          onClose();
        }
      }}
    >
      <div className="lw-template-picker flex h-[85vh] w-[85vw] max-w-6xl flex-col rounded-lg bg-card shadow-xl">
        {/* Header */}
        <div className="lw-template-picker-header flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-foreground">
              Choose a Template
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Start with a pre-designed template or create from scratch
            </p>
            {error && (
              <div className="mt-2 rounded-md bg-destructive/10 border border-destructive/20 p-2">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isCreating}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close template picker"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Filters and Search */}
        <div className="lw-template-picker-filters border-b border-border px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-foreground">
                Category:
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All Templates</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lw-template-picker-content flex-1 overflow-y-auto p-6">
          {templatesLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
                <p className="text-sm text-muted-foreground">
                  Loading templates...
                </p>
              </div>
            </div>
          ) : (
            <div className="lw-template-picker-grid grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {/* Blank Template Card */}
              <button
                onClick={handleCreateBlank}
                disabled={isCreating}
                className="group relative flex flex-col overflow-hidden rounded-lg border-2 border-dashed border-border bg-background p-6 text-left transition-all hover:border-primary hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex h-48 items-center justify-center rounded-md bg-muted">
                  <svg
                    className="h-16 w-16 text-muted-foreground group-hover:text-primary transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
                <div className="mt-4">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    Blank Email
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Start from scratch with an empty canvas
                  </p>
                </div>
              </button>

              {/* Template Cards */}
              {filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  disabled={isCreating}
                  className={`group relative flex flex-col overflow-hidden rounded-lg border-2 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    selectedTemplate === template.id
                      ? "border-primary bg-accent"
                      : "border-border bg-background hover:border-primary/50 hover:bg-accent"
                  }`}
                >
                  {/* Preview Image */}
                  <div className="relative h-48 overflow-hidden rounded-t-md bg-muted">
                    {template.previewImageUrl ? (
                      <img
                        src={template.previewImageUrl}
                        alt={template.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <svg
                          className="h-12 w-12 text-muted-foreground"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                    
                    {/* Selected Indicator */}
                    {selectedTemplate === template.id && (
                      <div className="absolute right-2 top-2 rounded-full bg-primary p-1">
                        <svg
                          className="h-4 w-4 text-primary-foreground"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}

                    {/* System Template Badge */}
                    {template.isSystemTemplate && (
                      <div className="absolute left-2 top-2 rounded-md bg-primary/90 px-2 py-1 text-xs font-medium text-primary-foreground">
                        System
                      </div>
                    )}
                  </div>

                  {/* Template Info */}
                  <div className="flex flex-col gap-1 p-4">
                    <h3 className="font-semibold text-foreground line-clamp-1">
                      {template.name}
                    </h3>
                    {template.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="rounded-md bg-muted px-2 py-1">
                        {template.category}
                      </span>
                      {template.usageCount > 0 && (
                        <span>Used {template.usageCount} times</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!templatesLoading && filteredTemplates.length === 0 && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-4 text-sm font-semibold text-foreground">
                  No templates found
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="lw-template-picker-footer flex items-center justify-between border-t border-border px-6 py-4">
          <div className="text-sm text-muted-foreground">
            {selectedTemplate
              ? `${templates.find((t) => t.id === selectedTemplate)?.name} selected`
              : "Select a template to continue"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isCreating}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateFromTemplate}
              disabled={!selectedTemplate || isCreating}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-r-transparent" />
                  Creating...
                </>
              ) : (
                "Use Template"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
